import { createContext, useState, useEffect } from "react";
import { callMsGraph } from "./graph";
import axios from 'axios';
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
import { setGlobalContextValues } from "./axiosConfig";
import { RumInit } from "@maersk-global/telemetry-web-sdk";
import { DateTime } from 'luxon';


const faro = RumInit({
    app: {
        name: "FLUX-PROD",
        version: "1.0.0",
    },
    apiKey: process.env.REACT_APP_RUM_API_KEY,
    debug: false, // false: Prevent showing log,error,etc captured in browser console
    traceLog: false, // Sends Traces to Loki ( For easy querying of traces)
    instrumentations: {
        fetch: true,
        console: false,
        error: false,
        interactions: true,
    },
});

// Create a context for the table data and related functions
export const TableContext = createContext(null);

/**
 * Context provider component for managing global state and functions.
 * @param {Object} props - The component props.
 * @param {React.ReactNode} props.children - The child components.
 */
export default function Context({ children }) {
    const { instance, accounts } = useMsal();
    const [graphData, setGraphData] = useState(null);
    const [token, setToken] = useState("");
    const [tableData, setTableData] = useState([]);
    const [cicdTableData, setCICDTableData] = useState([]);
    const [tokenFetched, setTokenFetched] = useState(false);
    const [search, setSearch] = useState(''); // Add search state
    const [selectedTime, setSelectedTime] = useState({ start: new Date(), end: new Date() }); // Add selectedTime state
    const [loading, setLoading] = useState(true); // Add loading state
    const [subscriptionsLoading, setSubscriptionsLoading] = useState(true);
    const [subscribeOpen, setSubscribeOpen] = useState(false);
    const [inputValue, setInputValue] = useState(''); // State to track entire input value
    const [activeTab, setActiveTab] = useState("Notification Center");//active sub-tab for Notifications
    const [mySubscriptionsOpen, setMySubscriptionsOpen] = useState(false);
    const [settingsLoading, setSettingsLoading] = useState(true);
    const [notificationsLoading, setNotificationsLoading] = useState(true);
    const [activeHomeTab, setActiveHomeTab] = useState("SNOW Changes");
    const [searchType, setSearchType] = useState('application'); // Track search type for API calls
    const [subscriptions, setSubscriptions] = useState([]);
    const [userPreferences, setUserPreferences] = useState({});
    const [vendorChangeTableData, setVendorChangeTableData] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [notificationCount, setNotificationCount] = useState(0);
    const [p1p2IncidentsData, setP1P2IncidentsData] = useState([]);
    const [activeP1P2Tab, setActiveP1P2Tab] = useState("All P1/P2 Incidents");
    const [userRole, setUserRole] = useState(null);
    const [redGreenAnalytics, setRedGreenAnalytics] = useState(null);
    const [redGreenChannelData, setRedGreenChannelData] = useState([]);

    useEffect(() => {
        if (graphData && token) {
          getToken();
          
        }
        console.log("Graph Data changed",graphData );
      }, [graphData]);

    // Fetch user details when accounts change
    useEffect(() => {
        if (accounts && accounts.length > 0) {
            fetchUserDetails();
        }
    }, [accounts]);
    useEffect(() => {
    setGlobalContextValues(token, graphData, setTokenFetched);
  }, [token, graphData, setTokenFetched]);
    // Fetch subscriptions when token is fetched
    useEffect(() => {
        if (tokenFetched) {
            // getSubscriptions();
            // getNotifications();
            getNotificationsCount();
            getADToken();
        }
    }, [tokenFetched]);

    // // Fetch table data when selected time changes
    // useEffect(() => {
    //     if (tokenFetched) {
    //         getTableData();
    //     }
    // }, [selectedTime]);

    /**
     * Fetches user details using MSAL and Microsoft Graph API.
     */
    const fetchUserDetails = async () => {
        try {
            await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0],
            }).then((response) => {
                callMsGraph(response.accessToken).then((response) => {
                    setGraphData(response);
                });
                setToken(response.accessToken);
            });
        } catch (error) {
            console.log(error);
        }
        return true;
      };

    /**
     * Fetches the user role from the backend.
     */
    const getUserRole = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URI}/api/auth/get_user_role?user_email=${graphData?.mail}`, {
                headers: {
                    Authorization: `Bearer ${localStorage.getItem("jwt_token")}`
                }
            });
            setUserRole(response.data.role);
        } catch (error) {
            console.error('Error fetching user role:', error);
        }
    };

    /**
     * Fetches a JWT token from the backend server.
     */
    const getToken = async () => {
        console.trace("hello")
        setLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/api/auth/getToken`, { userEmail: graphData?.mail, userData: graphData }, {
                headers: {
                    Authorization: "Bearer " + token,
                }
            });
            localStorage.setItem("jwt_token", response.data.data);
            
            // Store user preferences
            setUserPreferences(response.data.preference || {});
            
            // Apply preference to selected time if available
            if (response.data.preference?.changes?.duration) {
                applyTimePreference(response.data.preference.changes.duration);
            }
            
            // Set user role from the response
            setUserRole(response.data.role);
            
            setTokenFetched(true);
            faro.api.setUser({
                id: graphData?.mail,
                email: graphData?.mail,
                username: graphData?.displayName,
            });
        } catch (error) {
            console.error(error);
        }
    };
    const getADToken = async () => {
        try {
          const response = await axios.get(`${process.env.REACT_APP_BACKEND_URI}/api/auth/getOnboardingToken`, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("jwt_token")}`
            }
          });
          
          if (response.status === 200) {
            const result = response.data;
            
            // Check if the backend returned success status and token
            if (result.status === "success" && result.token) {
              // Store the onboarding token in localStorage
              localStorage.setItem('azure_token', result.token);
              return result.token;
            } else {
              throw new Error("Failed to get valid token from backend");
            }
          } else {
            console.error("Failed to fetch onboarding token - HTTP status:", response.status);
            throw new Error(`HTTP error! status: ${response.status}`);
          }
        } catch (error) {
          if (error.message === "Timeout") {
            console.error("Request timed out while fetching onboarding token");
          } else {
            console.error("Error fetching onboarding token:", error);
          }
          throw error;
        }
      };

    /**
     * Apply time preference to selectedTime state
     */
    const applyTimePreference = (durationLabel) => {
        // Map duration labels to time calculations (same as Time.jsx timeOptions)
        const getTimeRange = (label) => {
            const now = DateTime.now();
            switch (label) {
                case "Current Time":
                    return { start: now.toISO(), end: now.toISO() };
                case "Last 5 mins":
                    return { start: now.minus({ minutes: 5 }).toISO(), end: now.toISO() };
                case "Last 10 mins":
                    return { start: now.minus({ minutes: 10 }).toISO(), end: now.toISO() };
                case "Last 30 mins":
                    return { start: now.minus({ minutes: 30 }).toISO(), end: now.toISO() };
                case "Last hour":
                    return { start: now.minus({ hours: 1 }).toISO(), end: now.toISO() };
                case "Last 2 hours":
                    return { start: now.minus({ hours: 2 }).toISO(), end: now.toISO() };
                case "Last 3 hours":
                    return { start: now.minus({ hours: 3 }).toISO(), end: now.toISO() };
                case "Last 4 hours":
                    return { start: now.minus({ hours: 4 }).toISO(), end: now.toISO() };
                case "Last 6 hours":
                    return { start: now.minus({ hours: 6 }).toISO(), end: now.toISO() };
                case "Last 12 hours":
                    return { start: now.minus({ hours: 12 }).toISO(), end: now.toISO() };
                case "Last day":
                    return { start: now.minus({ days: 1 }).toISO(), end: now.toISO() };
                case "Last 2 days":
                    return { start: now.minus({ days: 2 }).toISO(), end: now.toISO() };
                case "Last 3 days":
                    return { start: now.minus({ days: 3 }).toISO(), end: now.toISO() };
                case "Last 4 days":
                    return { start: now.minus({ days: 4 }).toISO(), end: now.toISO() };
                case "Last 5 days":
                    return { start: now.minus({ days: 5 }).toISO(), end: now.toISO() };
                case "Last week":
                    return { start: now.minus({ weeks: 1 }).toISO(), end: now.toISO() };
                case "Last 2 weeks":
                    return { start: now.minus({ weeks: 2 }).toISO(), end: now.toISO() };
                case "Last month":
                    return { start: now.minus({ months: 1 }).toISO(), end: now.toISO() };
                case "Upcoming 5 mins":
                    return { start: now.toISO(), end: now.plus({ minutes: 5 }).toISO() };
                case "Upcoming 10 mins":
                    return { start: now.toISO(), end: now.plus({ minutes: 10 }).toISO() };
                case "Upcoming 30 mins":
                    return { start: now.toISO(), end: now.plus({ minutes: 30 }).toISO() };
                case "Upcoming hour":
                    return { start: now.toISO(), end: now.plus({ hours: 1 }).toISO() };
                case "Upcoming 2 hours":
                    return { start: now.toISO(), end: now.plus({ hours: 2 }).toISO() };
                case "Upcoming 3 hours":
                    return { start: now.toISO(), end: now.plus({ hours: 3 }).toISO() };
                case "Upcoming 4 hours":
                    return { start: now.toISO(), end: now.plus({ hours: 4 }).toISO() };
                case "Upcoming 6 hours":
                    return { start: now.toISO(), end: now.plus({ hours: 6 }).toISO() };
                case "Upcoming 12 hours":
                    return { start: now.toISO(), end: now.plus({ hours: 12 }).toISO() };
                case "Upcoming day":
                    return { start: now.toISO(), end: now.plus({ days: 1 }).toISO() };
                case "Upcoming 2 days":
                    return { start: now.toISO(), end: now.plus({ days: 2 }).toISO() };
                case "Upcoming 3 days":
                    return { start: now.toISO(), end: now.plus({ days: 3 }).toISO() };
                case "Upcoming 4 days":
                    return { start: now.toISO(), end: now.plus({ days: 4 }).toISO() };
                case "Upcoming 5 days":
                    return { start: now.toISO(), end: now.plus({ days: 5 }).toISO() };
                case "Upcoming week":
                    return { start: now.toISO(), end: now.plus({ weeks: 1 }).toISO() };
                case "Upcoming 2 weeks":
                    return { start: now.toISO(), end: now.plus({ weeks: 2 }).toISO() };
                case "Upcoming month":
                    return { start: now.toISO(), end: now.plus({ months: 1 }).toISO() };
                default:
                    return null;
            }
        };

        const timeRange = getTimeRange(durationLabel);
        if (timeRange) {
            const utcStartDate = DateTime.fromISO(timeRange.start, { zone: 'UTC' }).toJSDate();
            const utcEndDate = DateTime.fromISO(timeRange.end, { zone: 'UTC' }).toJSDate();
            setSelectedTime({ start: utcStartDate, end: utcEndDate });
        }
    };

    /**
     * Apply user's time preference based on current moment when Changes page loads
     */
    const applyUserPreferenceOnPageLoad = () => {
        if (userPreferences?.changes?.duration) {
            applyTimePreference(userPreferences.changes.duration);
        }
    };

    /**
     * Handles user logout and clears the cache.
     */
    const handleLogout = async () => {
        try {
            // Ensure that 'instance' is your MSAL.js instance (UserAgentApplication or PublicClientApplication)
            // If using PublicClientApplication, you might not have handleRedirectCallback
            if (instance.handleRedirectCallback) {
                // Wait for any ongoing interaction to complete
                await instance.handleRedirectCallback();
            }
            // Replace 'accounts' with the correct account object
            const accounts = instance.getAllAccounts()[0]; // Retrieve the first account, adjust as needed
            // Perform the logout operation
            await instance.logoutRedirect({
                account: accounts,
                postLogoutRedirectUri: null,
            });
            // Clear the token cache after successful logout
            instance.clearCache();
        } catch (error) {
            console.error("Error during logout:", error);
            // Handle error as needed
        }
    };

    /**
     * Formats a date object to a string in the format YYYY-MM-DD HH:MM:SS (UTC).
     * @param {Date} date - The date object to format.
     * @returns {string} The formatted date string.
     */
    const getCurrentFormattedDateTime = (date) => {
        const padZero = (num) => (num < 10 ? `0${num}` : num);

        const year = date.getUTCFullYear();
        const month = padZero(date.getUTCMonth() + 1); // Months are zero-based
        const day = padZero(date.getUTCDate());
        const hours = padZero(date.getUTCHours());
        const minutes = padZero(date.getUTCMinutes());
        const seconds = padZero(date.getUTCSeconds());

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    /**
     * Fetches table data from the backend server based on the selected time and search value.
     */
    const getTableData = async (tabLabel) => {
        setLoading(true);
        const startTimeFormatted = getCurrentFormattedDateTime(selectedTime.start);
        const endTimeFormatted = getCurrentFormattedDateTime(selectedTime.end);
        const body = {
            first_param: startTimeFormatted,
            second_param: endTimeFormatted
        };
        const trimmedSearch = (search || '').trim();

        const shouldFetchFreelookSNOWChanges =
            tabLabel === "SNOW Changes" &&
            searchType === 'global' &&
            trimmedSearch.length > 0 &&
            startTimeFormatted === endTimeFormatted;

        if (shouldFetchFreelookSNOWChanges) {
            try {
                const response = await axios.post(
                    `${process.env.REACT_APP_BACKEND_URI}/api/change_data/freelookSNOWSearchChanges`,
                    {
                        ...body,
                        search_terms: trimmedSearch,
                    },
                    {
                        headers: {
                            Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                        }
                    }
                );

                const { data: responseData, status } = response.data || {};

                if (status === 200 && Array.isArray(responseData)) {
                    const transformedData = responseData.map((item, index) => ({
                        "Unique ID": `${item.change_number || 'change'}_${index}`,
                        "Change Number": item.change_number || "",
                        "Configuration Item": item.configuration_item || "",
                        "Risk": item.risk_impact || "",
                        "Short Description": item.short_description || "",
                        "Planned Start Date": item.planned_start_date || "",
                        "Planned End Date": item.planned_end_date || "",
                        "State": item.state || "",
                        "Type": item.type || "",
                        "Assignment Group": item.assignment_group || "",
                        "Requested By": item.requested_by || "",
                        "Close Code": item.close_code || "",
                        "Category": item.change_type || item.configuration_item_class || "",
                        "CI Environment": item.ci_environment || "",
                        "Description": item.description || "",
                        "Configuration Item Class": item.configuration_item_class || "",
                    }));

                    setTableData(transformedData);

                    if (transformedData.length === 0) {
                        toast.error("No SNOW changes found for the provided search terms", {
                            position: "top-center",
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: "light"
                        });
                    }
                } else {
                    setTableData([]);
                    toast.error("No SNOW changes found for the provided search terms", {
                        position: "top-center",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light"
                    });
                }
            } catch (error) {
                console.error("Error fetching SNOW changes for freelook search:", error);
                setTableData([]);
                toast.error("Failed to fetch SNOW changes for the provided search terms", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light"
                });
            } finally {
                setLoading(false);
            }
            return;
        }

        try {
            if(tabLabel === "Vendor Changes"){
                try {
                    let apiRoute = "getAllVendorChanges";
                    // Check if search is active for application search
                    if (search !== '' && searchType === 'application') {
                        body.appname = search;
                        apiRoute = "getSearchedVendorChanges";
                    }

                    const response = await axios.post(`${process.env.REACT_APP_VENDOR_URI}/vendor_changes/${apiRoute}`, body, {
                        headers: {
                            Authorization: "Bearer " + localStorage.getItem("azure_token"),
                        }
                    });
                    const vendorData = response.data;

                    // Check if response indicates no data found
                    if (vendorData.status === 200 && vendorData.data?.error === "No vendor changes found matching the criteria") {
                        setVendorChangeTableData([]);
                        toast.error("No vendor changes found", {
                            position: "top-center",
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: "light"
                        });
                        return;
                    }

                    if (vendorData.status === 200 && vendorData.data) {
                        // Transform the API response to match the expected data structure
                        const transformedData = vendorData.data.map((item, index) => {
                            // Convert ISO datetime to yyyy-MM-dd HH:mm:ss format
                            const formatDateTime = (isoString) => {
                                if (!isoString) return "";
                                try {
                                    const dt = DateTime.fromISO(isoString, { zone: 'utc' });
                                    return dt.isValid ? dt.toFormat('yyyy-MM-dd HH:mm:ss') : "";
                                } catch {
                                    return "";
                                }
                            };
                            const extractDays = (durationString) => {
                                if (!durationString) return "";
                                const daysMatch = durationString.match(/(\d+)\s*days?/i);
                                return daysMatch ? `${daysMatch[1]} day${daysMatch[1] !== '1' ? 's' : ''}` : durationString;
                            };
                            const uniqueId = `${item.change_id}_${index}_${item.start_time}`;

                            return {
                                "Unique ID": uniqueId,
                                "Change ID": item.change_id || "",
                                "Change Title": item.change_title || "",
                                "Category": item.category || "",
                                "Service Name": item.service_impacted || "",
                                "Region": item.region || "",
                                "Severity": item.severity || "",
                                "Stage": item.stage || "",
                                "Scheduled Start Time": formatDateTime(item.start_time),
                                "Scheduled End Time": formatDateTime(item.end_time),
                                "Affected Accounts": item.affected_accounts?.join(", ") || "",
                                "Application ID": item.application_ids?.join(", ") || "",
                                "Application Name": item.application_names?.join(", ") || "",
                                "Duration": extractDays(item.duration),
                                "Impact Summary": item.impact_summary || "",
                                "Notification Source": item.notification_source || "",
                                "Resource Name": item.resource_name || "",
                                "Grouped Stages": item.grouped_stages || [],
                            };
                        });

                        setVendorChangeTableData(transformedData);
                    } else {
                        setVendorChangeTableData([]);
                        toast.error("No vendor change data found", {
                            position: "top-center",
                            autoClose: 3000,
                            hideProgressBar: false,
                            closeOnClick: true,
                            pauseOnHover: true,
                            draggable: true,
                            progress: undefined,
                            theme: "light"
                        });
                    }
                } catch (error) {
                    console.error("Error fetching vendor changes:", error);
                    setVendorChangeTableData([]);
                    // For actual server errors (500, network errors, etc.)
                    toast.error("Failed to fetch vendor changes", {
                        position: "top-center",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light"
                    });
                } finally {
                    setLoading(false);
                }
                return;
            }
            let apiRoute;
            // Only call search APIs for application search, not for global search
            if (search !== '' && searchType === 'application') {
                body.appname = search;
                apiRoute = tabLabel === "SNOW Changes" ? "getSearchedSNOWChanges" : "getSearchedCICDChanges";
            } else {
                apiRoute = tabLabel === "SNOW Changes" ? "getAllSNOWChanges" : "getAllCICDChanges";
            }

            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/api/change_data/${apiRoute}`, body, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                }
            });
            const data = response.data;

            if (data.message) {
                tabLabel === "SNOW Changes" ? setTableData([]) : setCICDTableData([]);
                toast.error("No data found", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light"
                });
            } else {
                const cleanedData = data.filter(item =>
                    typeof item === 'object' && item !== null && !Array.isArray(item)
                );
                if (cleanedData.length > 0) {
                    tabLabel === "SNOW Changes" ? setTableData(cleanedData) : setCICDTableData(cleanedData);
                } else {
                    tabLabel === "SNOW Changes" ? setTableData([]) : setCICDTableData([]);
                    toast.error("No data found", {
                        position: "top-center",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light"
                    });
                }
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    /**
     * Fetches P1/P2 incidents data from the backend server.
     * Fetches all incidents at once and filters client-side based on tab selection.
     */
    const getP1P2IncidentsData = async () => {
        setLoading(true);
        try {
            const url = `${process.env.REACT_APP_BACKEND_URI}/api/change_data/p1p2Incidents`;
            
            const response = await axios.get(url, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                }
            });
            const responseData = response.data;

            if (responseData.message || !responseData.data || responseData.data.length === 0) {
                setP1P2IncidentsData([]);
                toast.error("No P1/P2 incidents found", {
                    position: "top-center",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light"
                });
            } else {
                const cleanedData = responseData.data.filter(item =>
                    typeof item === 'object' && item !== null && !Array.isArray(item)
                );
                if (cleanedData.length > 0) {
                    setP1P2IncidentsData(cleanedData);
                } else {
                    setP1P2IncidentsData([]);
                    toast.error("No P1/P2 incidents found", {
                        position: "top-center",
                        autoClose: 3000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "light"
                    });
                }
            }
        } catch (error) {
            console.error("Error fetching P1/P2 incidents:", error);
            setP1P2IncidentsData([]);
            toast.error("Failed to fetch P1/P2 incidents", {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                theme: "light"
            });
        } finally {
            setLoading(false);
        }
    };

    const getSubscriptions = async () => {
        setSubscriptionsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/api/subscription/getSubscriptions`, { user_email: graphData?.mail }, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                }
            });
            const data = response.data;
            if (data.status === 200) {
                setSubscriptions(data.subscriptions);
            } else if (data?.detail?.includes("User not found")) {
                console.log("User not found. Needs to subscribe to applications first.");
                setSubscriptions([]);
            } else {
                console.error('Failed to fetch subscriptions');
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSubscriptionsLoading(false);
        }
    };

    const getNotifications = async () => {
        setNotificationsLoading(true);
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/api/subscription/getNotifications`, { user_email: graphData?.mail }, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                }
            });
            const data = response.data;
            if (data?.status === 200) {
                console.log("Notifications: ", data.notifications);
                setNotifications(data.notifications);
            } else if (response?.detail?.includes("User not found")) {
                console.log("User not found. Needs to subscribe to applications first.");
                setNotifications([]);
            } else {
                console.error('Failed to fetch notifications');
                // toast.error("Failed to fetch Notifications", {
                //     position: "top-center",
                //     autoClose: 3000,
                //     hideProgressBar: false,
                //     closeOnClick: true,
                //     pauseOnHover: true,
                //     draggable: true,
                //     progress: undefined,
                //     theme: "light",
                // });
            }
        } catch (error) {
            console.error(error);
            // toast.error("Failed to fetch Notifications", {
            //     position: "top-center",
            //     autoClose: 3000,
            //     hideProgressBar: false,
            //     closeOnClick: true,
            //     pauseOnHover: true,
            //     draggable: true,
            //     progress: undefined,
            //     theme: "light",
            // });
        } finally {
            setNotificationsLoading(false);
        }
    };

    const getNotificationsCount = async () => {
        try {
            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URI}/api/subscription/getNotificationsCount`, { user_email: graphData?.mail }, {
                headers: {
                    Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                }
            });
            const data = response.data;
            if (data?.status === 200) {
                setNotificationCount(data.count);
                console.log("Notification count:", data.count);
            } else {
                console.error('Failed to fetch notification count');
            }
        } catch (error) {
            console.error(error);
        }
    };
    
    const sendUserInsights = async (data) => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_BACKEND_URI}/api/change_data/storeuserinsights`,
                data,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    },
                }
            );
            console.log("Insights API called with response", response.data);
        } catch (error) {
            if (error.message === "Timeout") {
                console.log("Request Timed Out");
            }
            console.error("An error occurred:", error);
        }
    };

    /**
     * Fetches Red/Green Channel Analytics data
     */
    const getRedGreenAnalytics = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannelAnalytics`,
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    }
                }
            );
            if (response.data) {
                setRedGreenAnalytics(response.data);
            }
        } catch (error) {
            console.error("Error fetching red/green analytics:", error);
            toast.error("Failed to fetch analytics data", {
                position: "top-center",
                autoClose: 3000,
                theme: "light",
            });
        }
    };

    /**
     * Fetches Red/Green Channel data
     */
    const getRedGreenChannelData = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URI}/api/redGreenChannel`,
                {
                    headers: {
                        Authorization: "Bearer " + localStorage.getItem("jwt_token"),
                    }
                }
            );
            if (response.data) {
                setRedGreenChannelData(response.data);
            }
        } catch (error) {
            console.error("Error fetching red/green channel data:", error);
            toast.error("Failed to fetch channel data", {
                position: "top-center",
                autoClose: 3000,
                theme: "light",
            });
        }
    };

    



    return (
        <TableContext.Provider
            value={{
                graphData,
                setGraphData,
                token,
                setToken,
                tokenFetched,
                setTokenFetched,
                getToken,
                getTableData,
                tableData,
                setTableData,
                cicdTableData,
                setCICDTableData,
                search, // Provide search state
                setSearch, // Provide setSearch function
                fetchUserDetails,
                getCurrentFormattedDateTime,
                handleLogout,
                selectedTime,
                setSelectedTime,
                loading, // Provide loading state
                setLoading, // Provide setLoading function
                subscribeOpen,
                setSubscribeOpen,
                inputValue,
                setInputValue,
                activeTab,
                setActiveTab,
                subscriptions,
                mySubscriptionsOpen,
                setMySubscriptionsOpen,
                notifications,
                setNotifications,
                notificationCount,
                getNotificationsCount,
                getSubscriptions,
                getNotifications,
                settingsLoading,
                setSettingsLoading,
                notificationsLoading,
                setNotificationsLoading,
                activeHomeTab,
                setActiveHomeTab,
                searchType,
                setSearchType,
                subscriptionsLoading,
                setSubscriptionsLoading,
                userPreferences,
                setUserPreferences,
                applyTimePreference,
                applyUserPreferenceOnPageLoad,
                vendorChangeTableData,
                setVendorChangeTableData,
                p1p2IncidentsData,
                setP1P2IncidentsData,
                activeP1P2Tab,
                setActiveP1P2Tab,
                getP1P2IncidentsData,
                userRole,
                redGreenAnalytics,
                setRedGreenAnalytics,
                redGreenChannelData,
                setRedGreenChannelData,
                getRedGreenAnalytics,
                getRedGreenChannelData,
            }}>
            {children}
        </TableContext.Provider>
    );
}